// Pure panel positioning for ActivityDockExpandedPanel — edge-aware layout
// with viewport clamp so side-anchored cards stay fully on-screen.

import type { CSSProperties } from "react";
import { isHorizontalEdge, type DockEdge } from "./activityDockPositionMath";
import { activityDockPanelWidthPx } from "./activityDockPanelWidth";

export {
  ACTIVITY_DOCK_SIDE_PANEL_WIDTH_PX,
  ACTIVITY_DOCK_SIDE_PANEL_MIN_WIDTH_PX,
  activityDockPanelWidthPx,
} from "./activityDockPanelWidth";

export const ACTIVITY_DOCK_SIDE_PANEL_MAX_HEIGHT_VH = 0.85;
export const ACTIVITY_DOCK_HEDGE_PANEL_MAX_HEIGHT_VH = 0.65;

/** Vertical attach offset — keeps the card visually near the dock tab. */
export const ACTIVITY_DOCK_SIDE_PANEL_TAB_ATTACH_OFFSET_PX = 140;
export const ACTIVITY_DOCK_PANEL_VIEWPORT_MARGIN_PX = 8;

export type ActivityDockPanelStyleInput = {
  edge: DockEdge;
  offsetRatio: number;
  tabWidthPx: number;
  tabHeightPx: number;
  viewportW: number;
  viewportH: number;
  /** Optional sidebar width so top/bottom panels clear the rail. */
  sidebarW?: number;
};

/**
 * Clamp a side-anchored panel's `top` so `maxHeight` fits inside the viewport.
 * Uses the configured max-height fraction as the clamp budget (conservative).
 */
export function clampActivityDockSidePanelTopPx(args: {
  rawTopPx: number;
  viewportH: number;
  maxHeightVh: number;
  marginPx?: number;
}): number {
  const {
    rawTopPx,
    viewportH,
    maxHeightVh,
    marginPx = ACTIVITY_DOCK_PANEL_VIEWPORT_MARGIN_PX,
  } = args;
  if (viewportH <= 0) return rawTopPx;
  const maxPanelPx = viewportH * maxHeightVh;
  const minTop = marginPx;
  const maxTop = Math.max(minTop, viewportH - maxPanelPx - marginPx);
  return Math.min(Math.max(rawTopPx, minTop), maxTop);
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

function readShellSidebarWidthPx(): number {
  if (typeof window === "undefined") return 0;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--shell-sidebar-w")
    .trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Follow the pointer while the open panel is being repositioned. */
export function activityDockExpandedPanelDragStyle(args: {
  x: number;
  y: number;
  viewportW: number;
  viewportH: number;
  sidebarW?: number;
}): CSSProperties {
  const margin = ACTIVITY_DOCK_PANEL_VIEWPORT_MARGIN_PX;
  const sidebarW = args.sidebarW ?? readShellSidebarWidthPx();
  const widthPx = activityDockPanelWidthPx(args.viewportW);
  const maxH = args.viewportH * ACTIVITY_DOCK_SIDE_PANEL_MAX_HEIGHT_VH;
  const left = clamp(
    args.x - widthPx / 2,
    sidebarW + margin,
    Math.max(sidebarW + margin, args.viewportW - widthPx - margin),
  );
  const top = clamp(args.y - 24, margin, Math.max(margin, args.viewportH - maxH - margin));
  return {
    position: "fixed",
    left,
    top,
    width: widthPx,
    maxWidth: `calc(100vw - ${sidebarW + margin * 2}px)`,
    maxHeight: `${ACTIVITY_DOCK_SIDE_PANEL_MAX_HEIGHT_VH * 100}vh`,
  };
}

export function activityDockExpandedPanelStyle(
  input: ActivityDockPanelStyleInput,
): CSSProperties {
  const { edge, offsetRatio, tabWidthPx, tabHeightPx, viewportW, viewportH } = input;
  const widthPx = activityDockPanelWidthPx(viewportW);
  const margin = ACTIVITY_DOCK_PANEL_VIEWPORT_MARGIN_PX;
  const sidebarW = input.sidebarW ?? readShellSidebarWidthPx();

  if (isHorizontalEdge(edge)) {
    // Align horizontally with Firefly; clamp so the card stays on-screen.
    const usableW = Math.max(0, viewportW - sidebarW - tabWidthPx);
    const fireflyLeft = sidebarW + usableW * offsetRatio;
    const fireflyCenterX = fireflyLeft + tabWidthPx / 2;
    const left = clamp(
      fireflyCenterX - widthPx / 2,
      sidebarW + margin,
      Math.max(sidebarW + margin, viewportW - widthPx - margin),
    );
    return {
      position: "fixed",
      [edge]: tabHeightPx,
      left,
      width: widthPx,
      maxWidth: `calc(100vw - ${sidebarW + margin * 2}px)`,
      maxHeight: `${ACTIVITY_DOCK_HEDGE_PANEL_MAX_HEIGHT_VH * 100}vh`,
    };
  }

  const rawTopPx =
    offsetRatio * viewportH - ACTIVITY_DOCK_SIDE_PANEL_TAB_ATTACH_OFFSET_PX;
  const topPx = clampActivityDockSidePanelTopPx({
    rawTopPx,
    viewportH,
    maxHeightVh: ACTIVITY_DOCK_SIDE_PANEL_MAX_HEIGHT_VH,
  });

  if (edge === "left") {
    return {
      position: "fixed",
      left: `calc(var(--shell-sidebar-w, 0px) + ${tabWidthPx}px)`,
      top: topPx,
      width: widthPx,
      maxWidth: `calc(100vw - var(--shell-sidebar-w, 0px) - ${tabWidthPx + 16}px)`,
      maxHeight: `${ACTIVITY_DOCK_SIDE_PANEL_MAX_HEIGHT_VH * 100}vh`,
    };
  }

  return {
    position: "fixed",
    [edge]: tabWidthPx,
    top: topPx,
    width: widthPx,
    maxWidth: `calc(100vw - ${tabWidthPx + 16}px)`,
    maxHeight: `${ACTIVITY_DOCK_SIDE_PANEL_MAX_HEIGHT_VH * 100}vh`,
  };
}
